import { useMemo, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import {
  CANONICAL_SUBSEGMENTS,
  CANONICAL_SEGMENT_GROUPS,
  SUBSEGMENT_GROUP,
  type CanonicalSubsegment,
} from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { MARKET_PROVENANCE, PROVENANCE_BY_CODE } from '../market-provenance.js';
import type { PanelId, TabId } from '../store.js';
import { EUROPE_SHAPES, EUROPE_VIEWBOX } from './europe-map-data.js';

// Coverage — an informative Reference panel. Two tabs:
//   • Markets — a non-zoomable, inline choropleth of Europe with all 27 served
//     markets shaded, tiered by pipeline kind (register pipeline vs
//     commissioned dataset — see market-provenance.ts).
//   • Sectors — the sub-sector funnel: every market's raw / fine sub-sector
//     normalises INTO one of the 17 canonical sub-segments.
//
// Everything is driven off @hc/shared (markets.ts + taxonomy.ts) and the
// shared provenance table, so the panel can never drift from the vocabulary
// the Screener actually filters on.

// ── Markets tab ─────────────────────────────────────────────────────────────

// The two visual tiers reuse the existing target/expansion styling:
// 'target' = register-pipeline markets, 'expansion' = commissioned datasets.
type Tier = 'target' | 'expansion' | 'none';

const PIPELINE_MARKETS = MARKET_PROVENANCE.filter((m) => m.kind === 'pipeline');
const COMMISSIONED_MARKETS = MARKET_PROVENANCE.filter((m) => m.kind === 'commissioned');

function tierFor(iso: string): Tier {
  const p = PROVENANCE_BY_CODE.get(iso);
  if (!p) return 'none';
  return p.kind === 'pipeline' ? 'target' : 'expansion';
}

function MarketsTab() {
  const [hover, setHover] = useState<string | null>(null);

  const hoveredProv = hover ? PROVENANCE_BY_CODE.get(hover) : undefined;
  const hovered = hoveredProv ? { name: hoveredProv.name, kind: hoveredProv.kind } : null;

  return (
    <div className="cov-markets">
      <div className="cov-map-wrap">
        <svg
          viewBox={EUROPE_VIEWBOX}
          className="cov-map"
          role="img"
          aria-label="Map of Europe with covered markets shaded"
          preserveAspectRatio="xMidYMid meet"
        >
          {EUROPE_SHAPES.map((s) => {
            const tier = tierFor(s.iso);
            return (
              <path
                key={s.iso}
                d={s.d}
                className={`cov-cty cov-${tier}${hover === s.iso ? ' cov-hl' : ''}`}
                onMouseEnter={tier === 'none' ? undefined : () => setHover(s.iso)}
                onMouseLeave={tier === 'none' ? undefined : () => setHover(null)}
              >
                <title>{s.name}</title>
              </path>
            );
          })}
        </svg>
        <div className="cov-tip" aria-hidden={!hovered}>
          {hovered ? (
            <>
              <span className="cov-tip-name">{hovered.name}</span>
              <span className="cov-tip-meta">
                Served market ·{' '}
                {hovered.kind === 'pipeline' ? 'register pipeline' : 'commissioned dataset'}
              </span>
            </>
          ) : (
            <span className="cov-tip-meta">Hover a shaded country</span>
          )}
        </div>
      </div>

      <aside className="cov-side">
        <div className="cov-legend">
          <div className="cov-leg-row">
            <span className="cov-sw cov-sw-target" /> Register pipeline
            <span className="cov-leg-n">{PIPELINE_MARKETS.length}</span>
          </div>
          <div className="cov-leg-row">
            <span className="cov-sw cov-sw-expansion" /> Commissioned dataset
            <span className="cov-leg-n">{COMMISSIONED_MARKETS.length}</span>
          </div>
          <div className="cov-leg-row cov-leg-muted">
            <span className="cov-sw cov-sw-none" /> Out of scope
          </div>
        </div>

        <div className="cov-grp-h">Register pipeline</div>
        <ul className="cov-list">
          {PIPELINE_MARKETS.map((m) => (
            <li
              key={m.code}
              className={hover === m.code ? 'cov-li-hl' : ''}
              onMouseEnter={() => setHover(m.code)}
              onMouseLeave={() => setHover(null)}
            >
              <span className="cov-iso">{m.code}</span>
              <span className="cov-nm">{m.name}</span>
            </li>
          ))}
        </ul>

        <div className="cov-grp-h">Commissioned datasets</div>
        <ul className="cov-list">
          {COMMISSIONED_MARKETS.map((m) => (
            <li
              key={m.code}
              className={hover === m.code ? 'cov-li-hl' : ''}
              onMouseEnter={() => setHover(m.code)}
              onMouseLeave={() => setHover(null)}
            >
              <span className="cov-iso">{m.code}</span>
              <span className="cov-nm">{m.name}</span>
            </li>
          ))}
        </ul>

        <p className="cov-foot">
          All <b>{MARKET_PROVENANCE.length} markets</b> are served with equal focus.{' '}
          <b>Register-pipeline</b> markets are ingested directly from the national company register
          by our own services; <b>commissioned-dataset</b> markets are research-built from the
          national registers and loaded only with join-verified registry ids. Depth still varies by
          market — the per-market source roster is in Sources.
        </p>
      </aside>
    </div>
  );
}

// ── Sectors tab ─────────────────────────────────────────────────────────────

// A fine/raw sub-sector node: a market-agnostic clinical / operational service
// line and the canonical sub-segment it normalises INTO. This is the broader
// "raw" vocabulary a practitioner thinks in (imaging, dialysis, fertility,
// cataract, nursing homes, assisted living, …) collapsed onto the 17 canonical
// sub-segments the Screener filters on. Not split by country — the national
// classifications (UK SIC, FR NAF, …) all crosswalk through these same lines.
interface FineNode {
  label: string;
  canonical: CanonicalSubsegment;
}

// Curated fine → canonical map. Placement reflects the OPERATING model, not the
// clinical specialty in the abstract: cataract / endoscopy sit under Hospitals
// & clinics because they run as day-case treatment centres; dialysis / fertility
// / oncology are Specialist medical because they run as standalone specialist
// providers. Listed grouped by canonical for readability; render order is
// recomputed by the layout.
const FINE_SUBSECTORS: ReadonlyArray<FineNode> = [
  // Hospitals & clinics
  { label: 'General / acute hospital', canonical: 'Hospitals & clinics' },
  { label: 'Private hospital', canonical: 'Hospitals & clinics' },
  { label: 'Day-surgery / treatment centre', canonical: 'Hospitals & clinics' },
  { label: 'Cataract & ophthalmic surgery', canonical: 'Hospitals & clinics' },
  { label: 'Endoscopy unit', canonical: 'Hospitals & clinics' },
  { label: 'Outpatient clinic / polyclinic', canonical: 'Hospitals & clinics' },
  // Primary care (GP)
  { label: 'GP / family practice', canonical: 'Primary care (GP)' },
  { label: 'Urgent care / walk-in centre', canonical: 'Primary care (GP)' },
  { label: 'Out-of-hours primary care', canonical: 'Primary care (GP)' },
  { label: 'Occupational health', canonical: 'Primary care (GP)' },
  // Specialist medical
  { label: 'Oncology / cancer care', canonical: 'Specialist medical' },
  { label: 'Dialysis / renal care', canonical: 'Specialist medical' },
  { label: 'Fertility / IVF', canonical: 'Specialist medical' },
  { label: 'Cardiology', canonical: 'Specialist medical' },
  { label: 'Dermatology', canonical: 'Specialist medical' },
  { label: 'Orthopaedics', canonical: 'Specialist medical' },
  { label: "Women's health / gynaecology", canonical: 'Specialist medical' },
  { label: 'Mental health / psychiatry', canonical: 'Specialist medical' },
  { label: 'Aesthetics / cosmetic medicine', canonical: 'Specialist medical' },
  { label: 'Pain management', canonical: 'Specialist medical' },
  { label: 'ENT / sleep', canonical: 'Specialist medical' },
  // Dental
  { label: 'General dental practice', canonical: 'Dental' },
  { label: 'Orthodontics', canonical: 'Dental' },
  { label: 'Implants / oral surgery', canonical: 'Dental' },
  { label: 'Dental laboratory', canonical: 'Dental' },
  // Optical
  { label: 'Optometry / eyewear retail', canonical: 'Optical' },
  { label: 'Contact lenses', canonical: 'Optical' },
  // Audiology
  { label: 'Hearing aids / dispensing', canonical: 'Audiology' },
  { label: 'Audiology clinic', canonical: 'Audiology' },
  // Physiotherapy & allied health
  { label: 'Physiotherapy', canonical: 'Physiotherapy & allied health' },
  { label: 'Chiropractic', canonical: 'Physiotherapy & allied health' },
  { label: 'Osteopathy', canonical: 'Physiotherapy & allied health' },
  { label: 'Podiatry / chiropody', canonical: 'Physiotherapy & allied health' },
  { label: 'Occupational therapy', canonical: 'Physiotherapy & allied health' },
  { label: 'Speech & language therapy', canonical: 'Physiotherapy & allied health' },
  { label: 'Dietetics / nutrition', canonical: 'Physiotherapy & allied health' },
  { label: 'Nursing & midwifery services', canonical: 'Physiotherapy & allied health' },
  // Diagnostics & labs
  { label: 'Imaging (MRI / CT / X-ray)', canonical: 'Diagnostics & labs' },
  { label: 'Pathology / clinical labs', canonical: 'Diagnostics & labs' },
  { label: 'Phlebotomy / blood testing', canonical: 'Diagnostics & labs' },
  { label: 'Genetic / molecular testing', canonical: 'Diagnostics & labs' },
  { label: 'Health screening / checks', canonical: 'Diagnostics & labs' },
  // Pharmacy
  { label: 'Community pharmacy', canonical: 'Pharmacy' },
  { label: 'Online / mail-order pharmacy', canonical: 'Pharmacy' },
  { label: 'Hospital / clinical pharmacy', canonical: 'Pharmacy' },
  { label: 'Compounding / specials', canonical: 'Pharmacy' },
  // Pharma wholesale
  { label: 'Pharmaceutical wholesale', canonical: 'Pharma wholesale' },
  { label: 'Pre-wholesale / distribution', canonical: 'Pharma wholesale' },
  // Medical equipment & supplies
  { label: 'Mobility & orthopaedic aids', canonical: 'Medical equipment & supplies' },
  { label: 'Surgical / medical supplies', canonical: 'Medical equipment & supplies' },
  { label: 'Home medical equipment', canonical: 'Medical equipment & supplies' },
  { label: 'Prosthetics & orthotics', canonical: 'Medical equipment & supplies' },
  // Veterinary
  { label: 'Companion-animal practice', canonical: 'Veterinary' },
  { label: 'Veterinary referral hospital', canonical: 'Veterinary' },
  { label: 'Equine / farm / large-animal', canonical: 'Veterinary' },
  { label: 'Veterinary diagnostics', canonical: 'Veterinary' },
  // Residential elderly care
  { label: 'Nursing home (with nursing)', canonical: 'Residential elderly care' },
  { label: 'Residential care home', canonical: 'Residential elderly care' },
  { label: 'Dementia / memory care', canonical: 'Residential elderly care' },
  { label: 'Assisted living / extra-care', canonical: 'Residential elderly care' },
  { label: 'Retirement living', canonical: 'Residential elderly care' },
  // Residential specialist care
  { label: 'Learning disability / autism', canonical: 'Residential specialist care' },
  { label: 'Mental health residential', canonical: 'Residential specialist care' },
  { label: 'Substance misuse / rehab', canonical: 'Residential specialist care' },
  { label: "Children's residential care", canonical: 'Residential specialist care' },
  { label: 'Brain injury / neuro-rehab', canonical: 'Residential specialist care' },
  // Domiciliary / home care
  { label: 'Home / domiciliary care', canonical: 'Domiciliary / home care' },
  { label: 'Live-in care', canonical: 'Domiciliary / home care' },
  { label: 'Supported living', canonical: 'Domiciliary / home care' },
  { label: 'Reablement', canonical: 'Domiciliary / home care' },
  { label: 'Hospice / palliative care', canonical: 'Domiciliary / home care' },
  // Ambulance / patient transport
  { label: 'Emergency ambulance', canonical: 'Ambulance / patient transport' },
  { label: 'Non-emergency patient transport', canonical: 'Ambulance / patient transport' },
  { label: 'Medical repatriation', canonical: 'Ambulance / patient transport' },
  // Other healthcare
  { label: 'Healthcare IT / digital health', canonical: 'Other healthcare' },
  { label: 'Telehealth', canonical: 'Other healthcare' },
  { label: 'Wellness / complementary', canonical: 'Other healthcare' },
  { label: 'Blood / tissue services', canonical: 'Other healthcare' },
  { label: 'Childcare & social', canonical: 'Other healthcare' },
];

// One Corryk categorical token per segment group — the five editorial accents
// from tokens.css (link=Oxford blue, positive=forest green, accent=terracotta
// brand, claret, teal). Applied via `style` (not SVG presentation attributes)
// so the var() actually resolves on the <path>/<rect>.
const GROUP_COLOR: Record<string, string> = {
  'Hospitals & acute': 'var(--link)',
  'Primary, specialist & allied care': 'var(--positive)',
  'Pharmacy, diagnostics & supplies': 'var(--accent)',
  'Residential & home care': 'var(--claret)',
  Veterinary: 'var(--teal)',
};

function SectorsTab() {
  const fine = FINE_SUBSECTORS;

  // Layout: left column of fine nodes, right column of canonical nodes, ordered
  // by segment group so connectors stay short and the convergence reads as a
  // funnel. Canonical nodes sit at the mean-y of their feeders.
  const layout = useMemo(() => {
    const ROW = 26;
    const PAD = 3;
    const W = 820;
    const LX = 8; // left node x
    const LW = 296; // left node width
    const RX = 548; // right node x
    const RW = 256; // right node width

    // Order canonical by group, then by canonical order.
    const canonOrder = [...CANONICAL_SUBSEGMENTS].sort((a, b) => {
      const ga = CANONICAL_SEGMENT_GROUPS.indexOf(
        SUBSEGMENT_GROUP[a] as (typeof CANONICAL_SEGMENT_GROUPS)[number],
      );
      const gb = CANONICAL_SEGMENT_GROUPS.indexOf(
        SUBSEGMENT_GROUP[b] as (typeof CANONICAL_SEGMENT_GROUPS)[number],
      );
      if (ga !== gb) return ga - gb;
      return CANONICAL_SUBSEGMENTS.indexOf(a) - CANONICAL_SUBSEGMENTS.indexOf(b);
    });

    // Order fine nodes to match their canonical's order (so feeders cluster).
    const fineOrdered = [...fine].sort((a, b) => {
      const ca = canonOrder.indexOf(a.canonical);
      const cb = canonOrder.indexOf(b.canonical);
      if (ca !== cb) return ca - cb;
      return a.label.localeCompare(b.label);
    });

    const leftY = new Map<number, number>();
    fineOrdered.forEach((_, i) => leftY.set(i, PAD + i * ROW));
    const leftH = PAD + fineOrdered.length * ROW;

    // Canonical y = mean of its feeders' y (fall back to its own slot).
    const rightY = new Map<CanonicalSubsegment, number>();
    canonOrder.forEach((c) => {
      const ys = fineOrdered
        .map((f, i) => (f.canonical === c ? leftY.get(i)! : null))
        .filter((v): v is number => v != null);
      rightY.set(c, ys.length ? ys.reduce((s, v) => s + v, 0) / ys.length : PAD);
    });
    // De-overlap canonical nodes top→bottom in group order.
    let prev = -Infinity;
    canonOrder.forEach((c) => {
      const y = Math.max(rightY.get(c)!, prev + ROW);
      rightY.set(c, y);
      prev = y;
    });
    const lastCanon = canonOrder[canonOrder.length - 1];
    const rightH = (lastCanon ? (rightY.get(lastCanon) ?? 0) : 0) + ROW + PAD;
    const H = Math.max(leftH, rightH) + PAD;

    return {
      W,
      H,
      LX,
      LW,
      RX,
      RW,
      ROW,
      fineOrdered,
      canonOrder,
      leftY,
      rightY,
    };
  }, [fine]);

  const [active, setActive] = useState<{ kind: 'fine' | 'canon'; key: string } | null>(null);

  const isLinkActive = (f: FineNode) => {
    if (!active) return false;
    if (active.kind === 'fine') return active.key === f.label;
    return active.key === f.canonical;
  };

  const { W, H, LX, LW, RX, RW, ROW, fineOrdered, canonOrder, leftY, rightY } = layout;

  return (
    <div className="cov-sectors">
      <p className="cov-sec-intro">
        The <b>{FINE_SUBSECTORS.length} raw service lines</b> on the left — the granular categories
        operators actually trade in (imaging, dialysis, fertility, cataract, nursing homes, assisted
        living …) — normalise into the <b>{CANONICAL_SUBSEGMENTS.length} canonical sub-segments</b>{' '}
        the Screener filters and groups on. Each market&apos;s native classification (UK SIC, FR
        NAF, …) crosswalks through these same lines. Hover a node to trace its path.
      </p>
      <div className="cov-funnel-scroll">
        <svg viewBox={`0 0 ${W} ${H}`} className="cov-funnel" preserveAspectRatio="xMinYMin meet">
          {/* connectors */}
          {fineOrdered.map((f, i) => {
            const y1 = leftY.get(i)! + ROW / 2 - 4;
            const y2 = rightY.get(f.canonical)! + ROW / 2 - 4;
            const x1 = LX + LW;
            const x2 = RX;
            const mx = (x1 + x2) / 2;
            const color = GROUP_COLOR[SUBSEGMENT_GROUP[f.canonical]] ?? '#888';
            const on = isLinkActive(f);
            return (
              <path
                key={`c${i}`}
                d={`M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`}
                fill="none"
                style={{ stroke: color }}
                strokeWidth={on ? 1.8 : 1}
                strokeOpacity={active ? (on ? 0.9 : 0.06) : 0.28}
              />
            );
          })}

          {/* left: fine nodes */}
          {fineOrdered.map((f, i) => {
            const y = leftY.get(i)!;
            const color = GROUP_COLOR[SUBSEGMENT_GROUP[f.canonical]] ?? '#888';
            const on = isLinkActive(f);
            return (
              <g
                key={`f${i}`}
                transform={`translate(${LX} ${y})`}
                className="cov-node"
                onMouseEnter={() => setActive({ kind: 'fine', key: f.label })}
                onMouseLeave={() => setActive(null)}
              >
                <rect
                  width={LW}
                  height={ROW - 8}
                  rx={3}
                  className="cov-node-bg"
                  style={{ stroke: color, strokeOpacity: on ? 1 : 0.5 }}
                />
                <rect width={3} height={ROW - 8} rx={1.5} style={{ fill: color }} />
                <text x={12} y={(ROW - 8) / 2} className="cov-node-tx">
                  {f.label}
                </text>
              </g>
            );
          })}

          {/* right: canonical nodes */}
          {canonOrder.map((c) => {
            const y = rightY.get(c)!;
            const color = GROUP_COLOR[SUBSEGMENT_GROUP[c]] ?? '#888';
            const on = active?.kind === 'fine' ? false : active?.key === c;
            const dim =
              active != null &&
              !(active.kind === 'canon' && active.key === c) &&
              !(active.kind === 'fine' && fineByActive(active, fine).has(c));
            return (
              <g
                key={`r${c}`}
                transform={`translate(${RX} ${y})`}
                className="cov-node"
                onMouseEnter={() => setActive({ kind: 'canon', key: c })}
                onMouseLeave={() => setActive(null)}
              >
                <rect
                  width={RW}
                  height={ROW - 8}
                  rx={3}
                  className="cov-node-bg"
                  style={{
                    stroke: color,
                    strokeOpacity: on ? 1 : 0.6,
                    opacity: dim ? 0.4 : 1,
                  }}
                />
                <rect
                  width={4}
                  height={ROW - 8}
                  rx={2}
                  style={{ fill: color, opacity: dim ? 0.4 : 1 }}
                />
                <text
                  x={12}
                  y={(ROW - 8) / 2}
                  className="cov-node-tx cov-node-canon"
                  style={{ opacity: dim ? 0.45 : 1 }}
                >
                  {c}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="cov-grp-key">
        {CANONICAL_SEGMENT_GROUPS.map((g) => (
          <span key={g} className="cov-grp-pill">
            <span className="cov-sw" style={{ background: GROUP_COLOR[g] ?? '#888' }} />
            {g}
          </span>
        ))}
      </div>
      <p className="cov-foot">
        Placement reflects the <b>operating model</b>, not the specialty in the abstract — cataract
        and endoscopy sit under hospitals because they run as day-case treatment centres. The raw
        list is indicative, not exhaustive. Pure manufacturing (e.g. medical-device makers) is
        intentionally excluded.
      </p>
    </div>
  );
}

// Which canonical sub-segments a hovered fine node connects to (always 1, but
// kept as a set so the dim logic mirrors the connector logic).
function fineByActive(
  active: { kind: 'fine' | 'canon'; key: string },
  fine: ReadonlyArray<FineNode>,
): Set<CanonicalSubsegment> {
  const out = new Set<CanonicalSubsegment>();
  if (active.kind !== 'fine') return out;
  for (const f of fine) {
    if (f.label === active.key) out.add(f.canonical);
  }
  return out;
}

// ── Panel shell ─────────────────────────────────────────────────────────────

const TABS: Array<{ id: string; label: string; render: () => React.ReactNode }> = [
  { id: 'markets', label: 'Markets', render: MarketsTab },
  { id: 'sectors', label: 'Sectors', render: SectorsTab },
];

export function CoveragePanel({
  tabId,
  panelId,
  initialState,
}: {
  tabId: TabId;
  panelId: PanelId;
  initialState?: Record<string, unknown>;
}) {
  const [tab, setTab] = useState(() => {
    const want = initialState?.['initialTab'];
    return typeof want === 'string' && TABS.some((t) => t.id === want) ? want : 'markets';
  });
  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="cov-root">
        <style>{COV_CSS}</style>
        <Tabs.Root value={tab} onValueChange={setTab} className="cov-tabs">
          <Tabs.List className="cov-tablist">
            {TABS.map((t) => (
              <Tabs.Trigger key={t.id} value={t.id} className="cov-tab">
                {t.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {TABS.map((t) => (
            <Tabs.Content key={t.id} value={t.id} className="cov-body">
              {t.render()}
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </PanelFrame>
  );
}

const COV_CSS = `
.cov-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); }
.cov-tabs { display:flex; flex-direction:column; min-height:0; flex:1; }
.cov-tablist { display:flex; gap:16px; padding:8px var(--s-3) 0; border-bottom:1px solid var(--hairline); flex-wrap:wrap; }
.cov-tab { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-tertiary); background:none; border:none; border-bottom:2px solid transparent; padding:4px 0 6px; cursor:pointer; white-space:nowrap; }
.cov-tab[data-state="active"] { color:var(--text-primary); font-weight:600; border-bottom-color:var(--accent); }
.cov-body { flex:1; overflow:auto; padding:var(--s-4) var(--s-3); min-height:0; }

/* Markets tab */
.cov-markets { display:flex; gap:var(--s-4); align-items:flex-start; min-height:0; }
.cov-map-wrap { flex:1 1 auto; min-width:0; position:relative; }
.cov-map { width:100%; height:auto; max-height:72vh; display:block; }
.cov-cty { stroke:var(--bg); stroke-width:0.6; vector-effect:non-scaling-stroke; }
.cov-none { fill:var(--hover-subtle); }
.cov-target { fill:var(--accent); fill-opacity:0.82; cursor:pointer; transition:fill-opacity .1s; }
.cov-expansion { fill:var(--accent); fill-opacity:0.32; cursor:pointer; transition:fill-opacity .1s; }
.cov-target.cov-hl { fill-opacity:1; }
.cov-expansion.cov-hl { fill-opacity:0.55; }
.cov-tip { position:absolute; top:8px; left:8px; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:var(--radius-sm); padding:5px 9px; pointer-events:none; display:flex; flex-direction:column; line-height:1.3; min-width:120px; }
.cov-tip-name { font-weight:700; color:var(--text-primary); }
.cov-tip-meta { font-size:var(--t-meta); color:var(--text-tertiary); }

.cov-side { flex:0 0 232px; }
.cov-legend { display:flex; flex-direction:column; gap:5px; padding-bottom:12px; margin-bottom:8px; border-bottom:1px solid var(--hairline); }
.cov-leg-row { display:flex; align-items:center; gap:8px; font-size:var(--t-meta); color:var(--text-secondary); }
.cov-leg-row.cov-leg-muted { color:var(--text-tertiary); }
.cov-leg-n { margin-left:auto; font-variant-numeric:tabular-nums; font-weight:600; color:var(--text-primary); }
.cov-sw { width:12px; height:12px; border-radius:3px; flex:0 0 auto; }
.cov-sw-target { background:var(--accent); }
.cov-sw-expansion { background:var(--accent); opacity:0.34; }
.cov-sw-none { background:var(--hover-subtle); border:1px solid var(--hairline); }
.cov-grp-h { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.06em; color:var(--text-secondary); font-weight:700; margin:14px 0 5px; }
.cov-list { list-style:none; margin:0; padding:0; }
.cov-list li { display:flex; align-items:baseline; gap:8px; padding:3px 4px; border-radius:var(--radius-sm); cursor:default; }
.cov-list li.cov-li-hl { background:var(--hover-subtle); }
.cov-iso { font-variant-numeric:tabular-nums; font-weight:700; color:var(--text-primary); width:22px; flex:0 0 auto; }
.cov-nm { color:var(--text-secondary); flex:1 1 auto; }
.cov-reg { font-size:var(--t-meta); color:var(--text-tertiary); }
.cov-foot { margin:14px 0 0; font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.5; }
.cov-foot b { color:var(--text-secondary); font-weight:600; }

/* Sectors tab */
.cov-sectors { max-width:760px; }
.cov-sec-intro { margin:0 0 6px; line-height:1.55; color:var(--text-secondary); }
.cov-sec-intro b { color:var(--text-primary); font-weight:600; }
.cov-funnel-scroll { overflow:auto; }
.cov-funnel { width:100%; min-width:720px; height:auto; display:block; }
.cov-node { cursor:default; }
.cov-node-bg { fill:var(--surface, #fff); stroke-width:1; }
.cov-node-tx { font-size:11px; fill:var(--text-secondary); dominant-baseline:middle; }
.cov-node-canon { fill:var(--text-primary); font-weight:600; }
.cov-grp-key { display:flex; flex-wrap:wrap; gap:6px 14px; margin-top:8px; padding-top:8px; border-top:1px solid var(--hairline); }
.cov-grp-pill { display:flex; align-items:center; gap:6px; font-size:var(--t-meta); color:var(--text-secondary); }
`;
